<template>
    <mini-calculator-page>
        <div slot="summary-panel" class="flex-grid--vertical">
            <div class="p-animated-calc__results a-form-inputs u-full-width u-margin-auto">
                <mini-calculator-header title="Loss of Pension" icon="loss-of-pension"/>

                <div>
                    <control for="gender" label="Gender">
                        <select id="gender"
                                v-model="gender"
                                v-field-control
                                @change="handler"
                                required>
                            <option :value="null" disabled hidden>Please select...</option>
                            <option value="male">Male</option>
                            <option value="female">Female</option>
                        </select>
                        <control-error :show="$error('gender')">
                            The Gender is required
                        </control-error>
                    </control>
                    <control for="date_of_birth" label="Date of Birth">
                        <date-input ref="dateOfBirth"
                                    v-model="dateOfBirth"
                                    v-field-control:date_of_birth.required="{type:'date'}"
                                    @input="handler"></date-input>
                        <control-error :show="$error('date_of_birth')">
                            The date of birth is required and must be in the format DD/MM/YYYY
                        </control-error>
                    </control>
                    <control for="date_of_assumed_settlement" label="Date of Assumed Settlement/Trial">
                        <date-input ref="dateOfAssumedSettlement"
                                    v-model="dateOfAssumedSettlement"
                                    v-field-control:date_of_assumed_settlement.required="{type:'date'}"
                                    @input="handler"></date-input>
                        <control-error :show="$error('date_of_assumed_settlement')">
                            The date of assumed settlement/trial is required and must be in the format DD/MM/YYYY
                        </control-error>
                    </control>
                    <control for="pension_method" label="Simple Method or Longden Method?">
                        <select id="pension_method"
                                v-model="pensionMethod"
                                v-field-control
                                required>
                            <option :value="null" disabled hidden>Please select...</option>
                            <option value="simple">Simple</option>
                            <option value="longden" disabled>Longden (coming soon)</option>
                        </select>
                        <control-error :show="$error('pension_method')">
                            The Simple Method or Longden Method is required
                        </control-error>
                    </control>
                    <control for="lump_sum_payment" label="Is there a lump sum payment?">
                        <select id="lump_sum_loss"
                                v-model="lumpSumLoss"
                                v-field-control
                                @change="handler"
                                required>
                            <option :value="null" hidden disabled>Please select...</option>
                            <option :value="false">No</option>
                            <option :value="true">Yes</option>
                        </select>
                        <control-error :show="$error('lump_sum_payment')">
                            The lump sum payment is required
                        </control-error>
                    </control>
                    <control for="but_for_retirement_date"
                             :label="lumpSumLoss ? '\'But For\' Retirement Date/Date Lump Sum Would Have Been Taken' : '\'But For\' Retirement Date'">
                        <date-input v-model="butForRetirementDate"
                                    v-field-control:but_for_retirement_date.required="{type:'date'}"
                                    @update="handler"
                        ></date-input>
                        <control-error :show="$error('but_for_retirement_date')">
                            The 'But for' Retirement Date is required
                        </control-error>
                    </control>
                    <control for="but_for_retirement_age"
                             :label="lumpSumLoss ? '\'But For\' Retirement Age/Age Lump Sum Would Have Been Taken' : '\'But For\' Retirement Age'"
                             before-after="years">
                        <input autocomplete="off"
                               v-model.number="butForRetirementAge"
                               required
                               v-two-dp
                               min="0"
                               id="but_for_retirement_age"
                               @input="handler"
                               v-field-control:but_for_retirement_age.required="{type:'number', rules:'min:0.00'}"
                               type="number">
                        <control-error :show="$error('but_for_retirement_age')">
                            The 'But for' Retirement Age is required
                        </control-error>
                    </control>
                </div>

                <div v-if="lumpSumLoss">
                    <h2 class="u-r-margin-top-40">
                        Lump Sum Loss
                    </h2>

                    <control for="but_for_pension_lump_sum" label="'But For' Pension Lump Sum" before-after="pound">
                        <input autocomplete="off"
                               v-model.number="butForPensionLumpSum"
                               required
                               v-on:focus="butForPensionLumpSum = butForPensionLumpSum || ''"
                               v-on:blur="butForPensionLumpSum = butForPensionLumpSum || 0"
                               v-two-dp
                               min="0"
                               id="but_for_pension_lump_sum"
                               @input="handler"
                               v-field-control:but_for_pension_lump_sum.required="{type:'number', rules:'min:0.00'}"
                               type="number">
                        <control-error :show="!lumpSumValid">
                            'But For' Pension Lump Sum must be greater than the Lump Sum Payment After the Accident
                        </control-error>
                    </control>
                    <control for="pension_lump_sum" label="Actual Lump Sum Payment" before-after="pound">
                        <input autocomplete="off"
                               v-model.number="pensionLumpSum"
                               required
                               v-on:focus="pensionLumpSum = pensionLumpSum || ''"
                               v-on:blur="pensionLumpSum = pensionLumpSum || 0"
                               v-two-dp
                               min="0"
                               id="pension_lump_sum"
                               @input="handler"
                               v-field-control:pension_lump_sum.required="{type:'number', rules:'min:0.00'}"
                               type="number">
                        <control-error :show="!lumpSumValid">
                            Lump Sum Payment After the Accident must be less than the 'But For' Pension Lump Sum
                        </control-error>
                    </control>
                    <control for="date_lump_sum_received" label="Date Lump Sum Payment Received">
                        <date-input v-model="dateLumpSumPaymentReceived"
                                    v-field-control:date_lump_sum_received.required="{type:'date'}"
                                    @update="handler"
                        ></date-input>
                    </control>
                </div>

                <div>
                    <h2 class="u-r-margin-top-40">
                        Pension Losses
                    </h2>

                    <control for="but_for_retirement_date" label="'But For' Pension Annual Income" before-after="pound">
                        <input autocomplete="off"
                               v-model.number="butForPensionAnnual"
                               required
                               v-two-dp
                               v-on:focus="butForPensionAnnual = butForPensionAnnual || ''"
                               v-on:blur="butForPensionAnnual = butForPensionAnnual || 0"
                               min="0"
                               id="but_for_pension_annual"
                               @input="handler"
                               v-field-control:but_for_pension_annual.required="{type:'number', rules:'min:0.00'}"
                               type="number">
                        <control-error :show="!pensionAnnualIncomeValid">
                            'But For' Pension Annual Income must be greater than Actual Pension Annual Income
                        </control-error>
                    </control>

                    <control for="pension_annual" before-after="pound" label="Actual Pension Annual Income">
                        <input autocomplete="off"
                               v-model.number="pensionAnnual"
                               required
                               v-two-dp
                               v-on:focus="pensionAnnual = pensionAnnual || ''"
                               v-on:blur="pensionAnnual = pensionAnnual || 0"
                               min="0"
                               id="pension_annual"
                               @input="handler"
                               v-field-control:pension_annual.required="{type:'number', rules:'min:0.00'}"
                               type="number">
                        <control-error :show="!pensionAnnualIncomeValid">
                            Actual Pension Annual Income must be less than 'But For' Pension Annual Income
                        </control-error>
                    </control>
                </div>

                <h2 class="u-r-margin-top-40">
                    Result
                </h2>

                <control for="total_loss" label="Total Loss" before-after="pound-on-dark">
                    <input autocomplete="off" disabled="disabled"
                           id="total_loss" type="number"
                           name="total_loss"
                           v-two-dp
                           v-model="totalLoss">
                </control>
            </div>
        </div>

        <div slot="results-panel" class="flex-grid--vertical" v-show="totalLoss">
            <div class="p-animated-calc__results-panel u-full-width u-margin-auto">
                <h2>Explanation</h2>

                <h2>Pension Loss</h2>

                <p>
                    Based on the 'But For' retirement date (<strong>{{ butForRetirementDate }}</strong>) and Date of Assumed Settlement/Trial
                    (<strong>{{ dateOfAssumedSettlement }}</strong>).
                </p>
                <p>
                    The pension multiplier is <strong>{{ pensionLossMultiplier }}</strong>
                    <div @click="togglePensionMultiplierModal" style="margin-top: -40px; margin-left: 225px">
                        <icon-tooltip icon-type="formula" tooltip-text="Click to view calculations" side="right" text-width="140"></icon-tooltip>
                    </div>
                </p>

                <div v-if="lumpSumLoss">
                    <h2>Lump Sum Loss</h2>

                    <p>
                        Based on the Date Lump Sum Payment Received (<strong>{{ dateLumpSumPaymentReceived }}</strong>) the Period of Accelerated Receipt
                        is <strong>{{ acceleratedReceipt }}</strong> years.
                    </p>

                    <p>
                        Table 35 Discount Factor: <strong>{{ discountFactor }}</strong>
                        <div @click="toggleDiscountFactorModal" style="margin-top: -40px; margin-left: 240px">
                            <icon-tooltip icon-type="formula" tooltip-text="Click to view calculations" side="right" text-width="140"></icon-tooltip>
                        </div>
                    </p>

                    <p>
                        <h2>Lump Sum Loss Formula</h2>
                    </p>

                    <p>
                        <div class="m-formula">
                            <div class="m-formula__item--row">
                                <strong>
                                    Table 35 Discount Factor x ('But For' Pension Lump Sum - Actual Lump Sum Payment) = Lump Sum Loss
                                </strong>
                            </div>
                        </div>
                    </p>

                    <p>
                        <h2>Lump Sum Loss</h2>
                    </p>

                    <p>
                        <div class="m-formula">
                            <div class="m-formula__item--row">
                                <strong>
                                    {{ discountFactor }} x ({{ currencyFormat(butForPensionLumpSum) }} - {{ currencyFormat(pensionLumpSum) }}) = {{ currencyFormat(lumpSumLossPresentValue) }}
                                </strong>
                            </div>
                        </div>
                    </p>

                </div>

                <p>
                    <h2>Pension Loss Formula</h2>
                </p>

                <p>
                    <div class="m-formula">
                        <div class="m-formula__item--row">
                            <strong>
                                Pension Multiplier x ('But For' Pension Annual Income - Actual Pension Annual Income)
                                <span v-if="lumpSumLoss"> + Lump Sum Loss</span> = Total Annual Loss
                            </strong>
                        </div>
                    </div>
                </p>

                <p>
                    <h2>Pension Loss</h2>
                </p>

                <p>
                <div class="m-formula">
                    <div class="m-formula__item--row">
                        <strong>
                            {{ pensionLossMultiplier }} x ({{ currencyFormat(butForPensionAnnual) }} - {{ currencyFormat(pensionAnnual) }})
                            <span v-if="lumpSumLoss"> + {{ currencyFormat(lumpSumLossPresentValue) }}</span> = {{ currencyFormat(totalLoss) }}
                        </strong>
                    </div>
                </div>
                </p>

                <p>

                </p>
            </div>
            <modal name="pension_multiplier_modal" no-default-button="true">
                <modal-content></modal-content>
            </modal>

            <modal name="discount_factor_modal" no-default-button="true">
                <modal-content></modal-content>
            </modal>
        </div>
    </mini-calculator-page>
</template>

<script type="text/babel">
    import moment from 'moment';
    import {mapGetters, mapActions} from 'vuex';
    import MiniCalculatorPage from 'pages/mini-calculators/components/MiniCalculatorPage';
    import miniCalcIcons from 'components/Icons.vue';
    import Control from 'components/form/Control.vue';
    import ControlError from 'components/form/control/Error.vue';
    import FormValidator from 'components/form/validator/mixin';
    import DateInput from 'components/form/control/DateInput.vue';
    import TwoDecimalPlaces from 'directives/TwoDecimalPlaces';
    import Intro from 'pages/mini-calculators/calculators/CalculatorComponents/Intro';
    import CalculatorKeyBindings from 'pages/mini-calculators/calculators/mixins/MiniCalculatorBindings';
    import roundValue from 'mixins/roundValue';
    import MiniCalculatorHeader from 'pages/mini-calculators/components/MiniCalculatorHeader';
    import currencyFormat from 'mixins/currencyFormat';
    import Modal from 'components/modal/Modal';
    import ModalContent from 'components/modal/ModalContent';
    import IconTooltip from 'components/IconTooltip';

    export default {
        name: 'loss-of-pension',
        computed: {
            ...mapGetters('mini/lossOfPension', {
                detailValue: 'value',
                pensionValue: 'pensionValue',
                getPension: 'pension',
                pensionUserInputValid: 'isPensionUserInputValid',
                personUserInputValid: 'isPersonUserInputValid'
            }),
            gender: {
                get() {
                    return this.detailValue('gender');
                },
                set(value) {
                    this.updateDetail({key: 'gender', value});
                }
            },
            dateOfBirth: {
                get() {
                    return this.detailValue('dateOfBirth');
                },
                set(value) {
                    this.updateDetail({key: 'dateOfBirth', value});
                }
            },
            dateOfAssumedSettlement: {
                get() {
                    return this.detailValue('dateOfAssumedSettlement');
                },
                set(value) {
                    this.updateDetail({key: 'dateOfAssumedSettlement', value});
                }
            },
            pensionMethod: {
                get() {
                    return this.pensionValue('pensionMethod');
                },
                set(value) {
                    this.updatePensionValue({key: 'pensionMethod', value});
                }
            },
            lumpSumLoss: {
                get() {
                    return this.pensionValue('lumpSumLoss');
                },
                set(value) {
                    this.updatePensionValue({key: 'lumpSumLoss', value});
                }
            },
            butForRetirementDate: {
                get() {
                    return this.pensionValue('butForRetirementDate');
                },
                set(value) {
                    this.updatePensionValue({key: 'butForRetirementDate', value});
                }
            },
            butForRetirementAge: {
                get() {
                    return this.pensionValue('butForRetirementAge');
                },
                set(value) {
                    this.updatePensionValue({key: 'butForRetirementAge', value});
                }
            },
            butForPensionLumpSum: {
                get() {
                    return this.pensionValue('butForPensionLumpSum');
                },
                set(value) {
                    this.updatePensionValue({key: 'butForPensionLumpSum', value})
                        .then(() => this.setPension({key: 'lumpSumLossValue', value: this.butForPensionLumpSum - this.pensionLumpSum || 0}))
                }
            },
            pensionLumpSum: {
                get() {
                    return this.pensionValue('pensionLumpSum');
                },
                set(value) {
                    this.updatePensionValue({key: 'pensionLumpSum', value})
                        .then(() => this.updatePensionValue({key: 'lumpSumLossValue', value: this.butForPensionLumpSum - this.pensionLumpSum || 0}))
                }
            },
            dateLumpSumPaymentReceived: {
                get() {
                    return this.pensionValue('dateLumpSumPaymentReceived');
                },
                set(value) {
                    this.updatePensionValue({key: 'dateLumpSumPaymentReceived', value});
                }
            },
            butForPensionAnnual: {
                get() {
                    return this.pensionValue('butForPensionAnnual');
                },
                set(value) {
                    this.updatePensionValue({key: 'butForPensionAnnual', value})
                        .then(() => this.updatePensionValue({key: 'pensionLossAnnual', value: this.butForPensionAnnual - this.pensionAnnual || 0}))
                }
            },
            pensionAnnual: {
                get() {
                    return this.pensionValue('pensionAnnual');
                },
                set(value) {
                    this.updatePensionValue({key: 'pensionAnnual', value})
                        .then(() => this.updatePensionValue({key: 'pensionLossAnnual', value: this.butForPensionAnnual - this.pensionAnnual || 0}))
                }
            },
            pensionLossAnnual: {
                get() {
                    return this.pensionValue('pensionLossAnnual');
                },
                set(value) {
                    this.setPension({key: 'pensionLossAnnual', value});
                }
            },
            pensionLossMultiplier: {
                get() {
                    return this.pensionValue('pensionLossMultiplier');
                },
            },
            discountFactor: {
                get() {
                    return this.pensionValue('discountFactor');
                }
            },
            lumpSumLossPresentValue: {
                get() {
                    return this.pensionValue('lumpSumLossPresentValue');
                }
            },
            totalLoss: {
                get() {
                    return this.pensionValue('totalLoss');
                }
            },
            table21: {
                get() {
                    return this.detailValue('table21');
                }
            },
            table23: {
                get() {
                    return this.detailValue('table23');
                }
            },
            table27: {
                get() {
                    return this.detailValue('table27');
                }
            },
            lowerAge: {
                get() {
                    return this.detailValue('lowerAge');
                }
            },
            upperAge: {
                get() {
                    return this.detailValue('upperAge');
                }
            },
            lowerMultiplier: {
                get() {
                    return this.detailValue('lowerMultiplier');
                }
            },
            upperMultiplier: {
                get() {
                    return this.detailValue('upperMultiplier');
                }
            },
            acceleratedReceipt: {
                get() {
                    return this.pensionValue('acceleratedReceipt');
                }
            },
            lumpSumValid() {
                if (this.pensionLumpSum > this.butForPensionLumpSum) {
                    return false;
                }

                return true;
            },
            pensionAnnualIncomeValid() {
                if (this.pensionAnnual > this.butForPensionAnnual) {
                    return false;
                }

                return true;
            },
            isValid() {
                return this.pensionUserInputValid && this.personUserInputValid;
            },
            modalPayload() {
                return {
                    pension: this.getPension,
                    claimant: {
                        gender: this.detailValue('gender'),
                        dateOfBirth: this.detailValue('dateOfBirth')
                    },
                    claimInfo: {
                        dateOfAssumedSettlement: this.detailValue('dateOfAssumedSettlement'),
                        discountRate: "-0.75"
                    }
                };
            },
        },
        mixins: [
            FormValidator,
            CalculatorKeyBindings,
            roundValue,
            currencyFormat
        ],
        directives: {
            'two-dp': TwoDecimalPlaces
        },
        components: {
            Control,
            ControlError,
            DateInput,
            Intro,
            MiniCalculatorPage,
            MiniCalculatorHeader,
            miniCalcIcons,
            Modal,
            ModalContent,
            IconTooltip
        },
        created() {
            this.resetForm()
        },
        methods: {
            ...mapActions('mini/lossOfPension', {
                updateDetail: 'update',
                updatePensionValue: 'updatePensionValue',
                resetForm: 'reset',
                calculatePensionLoss: 'calculatePensionLoss',
                updateRetirement: 'updateRetirement'
            }),
            ...mapActions('modal', ['toggle', 'getModalContent']),
            handler: function () {
                this.updateRetirement();

                if (!this.isValid) {
                    return;
                }

                this.calculatePensionLoss();
                this.hidePrompt();
            },
            toggleDiscountFactorModal() {
                this.toggle('discount_factor_modal');

                this.getModalContent({
                    endpoint: 'lumpSumMultiplier',
                    payload: this.modalPayload
                });
            },
            togglePensionMultiplierModal() {
                this.toggle('pension_multiplier_modal');

                this.getModalContent({
                    endpoint: 'pensionLossMultiplier',
                    payload: this.modalPayload
                });
            },
        },
    }
</script>



// WEBPACK FOOTER //
// lossOfPension.vue?23171e44